import React, { createContext, useReducer, useContext, useMemo, useCallback } from 'react';

type ActionType = 'SET_SELECTED_ROW_IDS' | 'RESET_SELECTED' | 'SET_TOTAL_ROWS';

interface State {
  selectedRowIds: (string | number)[];
  totalRows: number;
}

const STATE: State = {
  selectedRowIds: [],
  totalRows: 0,
};

type Reducer = (state: State, action: { type: ActionType; payload?: any }) => typeof STATE;

interface BulkActionContextType extends State {
  resetSelection: () => void;
  setTotalRows: (total: number) => void;
  setSelectedRowIds: (selected: (string | number)[]) => void;
}

const BulkActionContext = createContext<BulkActionContextType>(null as any);

export const BulkActionContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer>((state, action) => {
    switch (action.type) {
      case 'SET_SELECTED_ROW_IDS':
        return { ...state, selectedRowIds: action.payload };
      case 'RESET_SELECTED':
        return { ...state, selectedRowIds: [] };
      case 'SET_TOTAL_ROWS':
        return { ...state, totalRows: action.payload };
      default:
        return { ...state };
    }
  }, STATE);

  // Memoize the functions to prevent recreation on every render
  const setSelectedRowIds = useCallback((ids: (string | number)[]) => {
    dispatch({ type: 'SET_SELECTED_ROW_IDS', payload: ids });
  }, []);

  const resetSelection = useCallback(() => {
    dispatch({ type: 'RESET_SELECTED' });
  }, []);

  const setTotalRows = useCallback((total: number) => {
    dispatch({ type: 'SET_TOTAL_ROWS', payload: total });
  }, []);

  const memoizedState = useMemo(
    () => ({
      ...state,
      setSelectedRowIds,
      resetSelection,
      setTotalRows,
    }),
    [state, setSelectedRowIds, resetSelection, setTotalRows]
  );

  return <BulkActionContext.Provider value={memoizedState}>{children}</BulkActionContext.Provider>;
};

export const useBulkActionContext = () => useContext(BulkActionContext);
